"use client";

import { useEffect, useState } from "react";
import { useCategoryStore } from "@/stores/categoryStore";
import { showToast } from "@/lib/toast";
import { CreateCategoryDto } from "@/services/categoryService";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface ModalCreateCategoryProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export default function ModalCreateCategory({
  isOpen,
  onClose,
  onComplete,
}: ModalCreateCategoryProps) {
  const { categories, fetchCategories, createCategory, isCreating } =
    useCategoryStore();

  const [form, setForm] = useState<CreateCategoryDto>({
    name: "",
    slug: "",
    parent: undefined,
    description: "",
    canonicalUrl: "",
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [canonicalTouched, setCanonicalTouched] = useState(false);
  const [errors, setErrors] = useState<Partial<CreateCategoryDto>>({});

  const slugify = (input: string): string => {
    return (
      input
        .toLowerCase()
        .normalize("NFD")
        // chuyển đ/Đ thành d
        .replace(/đ/g, "d")
        .replace(/Đ/g, "d")
        // loại bỏ dấu
        .replace(/\p{Diacritic}/gu, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
    );
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategories({ page: 1, limit: 100 });
      setForm({ name: "", slug: "", parent: undefined, description: "", canonicalUrl: "" });
      setErrors({});
      setSlugTouched(false);
      setCanonicalTouched(false);
    }
  }, [isOpen, fetchCategories]);

  const validate = () => {
    const e: Partial<CreateCategoryDto> = {};
    if (!form.name?.trim()) e.name = "Tên danh mục không được để trống";
    if (!form.slug?.trim()) e.slug = "Slug không được để trống";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    const ok = await createCategory({
      ...form,
      parent: form.parent || undefined,
    });
    if (ok) {
      showToast.success("Thành công", "Tạo danh mục thành công");
      onComplete();
      onClose();
    } else {
      showToast.error("Lỗi", "Không thể tạo danh mục");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
            Thêm danh mục
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Tên danh mục
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  setForm((p) => ({
                    ...p,
                    name: newName,
                    slug: slugTouched ? p.slug : slugify(newName),
                    canonicalUrl: canonicalTouched ? p.canonicalUrl : slugify(newName),
                  }));
                }}
                className={errors.name ? "border-red-500" : ""}
                disabled={isCreating}
              />
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="slug"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Slug
              </Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((p) => ({ ...p, slug: e.target.value }));
                }}
                className={errors.slug ? "border-red-500" : ""}
                disabled={isCreating}
              />
              {errors.slug && (
                <p className="text-red-500 text-sm">{errors.slug}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Danh mục cha (tùy chọn)
              </Label>
              <Select
                value={form.parent ?? "none"}
                onValueChange={(value: string) =>
                  setForm((p) => ({
                    ...p,
                    parent: value === "none" ? undefined : value,
                  }))
                }
                disabled={isCreating}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Chọn danh mục cha" />
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  <SelectItem value="none">Không chọn</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mô tả
              </label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 min-h-[90px]"
                disabled={isCreating}
              />
            </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Canonical URL (tùy chọn)
            </label>
            <input
              value={form.canonicalUrl || ""}
              onChange={(e) => {
                setCanonicalTouched(true);
                setForm((p) => ({ ...p, canonicalUrl: e.target.value }));
              }}
              placeholder="duong-dan-chuan"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
              type="text"
              inputMode="text"
              disabled={isCreating}
            />
          </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isCreating}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Đang tạo...
                </span>
              ) : (
                "Tạo danh mục"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
